import React from 'react';
import { List } from 'immutable';
import { GridList } from 'react-aria-components';

// Components
import CreateLabelListItem from './CreateLabelListItem';
import EmptyResultsListItem from './EmptyResultsListItem';
import LabelListSection from './LabelListSection';
import useLabelListData from '../hooks/useLabelListData';
import useLabelListSelection from '../hooks/useLabelListSelection';

// Utils
import { useTranslator } from '../../../../../../components/i18n/useTranslator';

// Types
import { Label } from '../../../../../../../common/labels/labelTypes';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import LabelListEmptyPlaceholder from './LabelListEmptyPlaceholder';

type Props = {
    handleToggleLabelSelection: (labelId: string) => void;
    handleCreateLabel: () => void;
    elements: List<ImMNElement>;
    searchString: string;
    allLabels: Label[];
    handleClickEdit?: (label: Label) => void;
    isSheet?: boolean;
};

const LabelLists = (props: Props) => {
    const {
        elements,
        searchString,
        allLabels,
        handleToggleLabelSelection,
        handleCreateLabel,
        handleClickEdit,
        isSheet = false,
    } = props;
    const { allLabelsFiltered, currentPathLabels, hasSearchResults, isSearching } = useLabelListData({
        allLabels,
        searchString,
    });
    const { checkedLabelIds, editingLabelId, gridListInteractionProps, handleEditingChange, handleSelectionChange } =
        useLabelListSelection({
            elements,
            handleCreateLabel,
            handleToggleLabelSelection,
        });
    const translate = useTranslator('popup', 'labels.menu');

    if (!isSearching && allLabels.length === 0) return <LabelListEmptyPlaceholder />;

    return (
        <div className="LabelLists">
            <GridList
                {...gridListInteractionProps}
                aria-label="Labels"
                selectedKeys={checkedLabelIds}
                onSelectionChange={handleSelectionChange}
                className="w-full min-w-0 overflow-x-hidden outline-hidden"
            >
                <CreateLabelListItem searchString={searchString} />

                {hasSearchResults && isSearching && (
                    <LabelListSection
                        checkedLabelIds={checkedLabelIds}
                        editingLabelId={editingLabelId}
                        isSheet={isSheet}
                        labels={allLabelsFiltered}
                        onEditingChange={handleEditingChange}
                        handleClickEdit={handleClickEdit}
                        sectionId="search-results-section"
                        showSeparator={true}
                    />
                )}

                {hasSearchResults &&
                    !isSearching &&
                    currentPathLabels.map(({ id, title, labels }, index) => (
                        <LabelListSection
                            key={id}
                            checkedLabelIds={checkedLabelIds}
                            editingLabelId={editingLabelId}
                            header={title}
                            isSheet={isSheet}
                            labels={labels}
                            onEditingChange={handleEditingChange}
                            sectionClassName={index === 0 && currentPathLabels.length > 1 ? 'pb-2' : undefined}
                            sectionId={`${id}-section`}
                            showSeparator={index > 0}
                            handleClickEdit={handleClickEdit}
                        />
                    ))}

                {!hasSearchResults && isSearching && <EmptyResultsListItem text={translate('noResults')} />}
            </GridList>
        </div>
    );
};

export default LabelLists;
